import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/admin/AdminContext";
import { PageHeader, StatusPill, EmptyState } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { money, FREQ_LABEL } from "@/lib/brand";
import { toast } from "sonner";
import { Package, Copy } from "lucide-react";

export default function Services() {
  const { version, openNewService, refresh } = useAdmin();
  const [services, setServices] = useState([]);

  const load = () => api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => { load(); }, [version]);

  const copyLink = async (s) => {
    try {
      const r = await api.post("/payments/checkout", { service_id: s.service_id, origin_url: window.location.origin });
      await navigator.clipboard.writeText(r.data.checkout_url);
      toast.success("Checkout link copied");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate link"); }
  };

  const visible = services.filter((s) => s.status !== "archived");

  return (
    <div className="animate-fade-up">
      <PageHeader title="Services" subtitle={`${visible.length} active`}>
        <Button onClick={() => openNewService()} data-testid="services-new-button"
          className="bg-[#67F2B3] hover:bg-[#4DE6A3] text-[#071A13] font-semibold rounded-lg">New Service</Button>
      </PageHeader>

      {visible.length === 0 ? (
        <EmptyState icon={Package} title="No services yet" hint="Create a custom service for any client in under a minute." />
      ) : (
        <div className="bg-white border border-[#E2EBF0] rounded-xl overflow-hidden" data-testid="services-list">
          {visible.map((s) => (
            <div key={s.service_id} data-testid={`service-row-${s.service_id}`}
              className="flex items-center justify-between gap-3 px-4 py-3.5 border-b border-[#E2EBF0] last:border-0 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#0D3B2D]">{s.name}</span>
                  <StatusPill status={s.payment_type === "subscription" ? (s.subscription_status || s.status) : s.status} />
                </div>
                <div className="text-xs text-[#4A635A] mt-0.5">{s.client_name}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-mono text-sm text-[#0D3B2D] text-right">
                  <div className="font-semibold">{money(s.amount)}</div>
                  <div className="text-[10px] text-[#4A635A]">
                    {s.payment_type === "subscription" ? FREQ_LABEL[s.frequency] : "one-time"}
                  </div>
                </div>
                <Button onClick={() => copyLink(s)} variant="outline" size="sm"
                  data-testid={`services-copy-${s.service_id}`} className="border-[#E2EBF0] rounded-lg text-xs">
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
